import React, { useState } from 'react';
import { PhenotypeGenotypeFit, ClinicalContext, CARDIAC_PHENOTYPE_LABELS } from '../types';

interface ClinicalFitCardProps {
  fit: PhenotypeGenotypeFit | null;
  clinicalContext: ClinicalContext | null;
}

const CONCORDANCE_STYLES: Record<string, { bg: string; border: string; text: string; label: string }> = {
  high: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', label: 'High Concordance' },
  moderate: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', label: 'Moderate Concordance' },
  low: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', label: 'Low Concordance' },
  insufficient_data: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-500', label: 'Insufficient Data' },
};

const ClinicalFitCard: React.FC<ClinicalFitCardProps> = ({ fit, clinicalContext }) => {
  const [showFactors, setShowFactors] = useState(false);

  // Don't render if no clinical context
  if (!clinicalContext || !clinicalContext.phenotype || clinicalContext.phenotype === 'none') {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <span>🔬</span> Patient Specific Phenotype Correlation
        </h3>
        <p className="text-sm text-slate-400 italic">
          Add clinical context above to assess phenotype-genotype concordance.
        </p>
      </div>
    );
  }

  if (!fit) {
    // Return null to hide the card if generation failed (as requested by user to avoid error text)
    // Or return a loading state if we had one.
    // User instruction: "make sure you never present text like 'no response generated, check logs'"
    return null;
  }

  const style = CONCORDANCE_STYLES[fit.concordance] || CONCORDANCE_STYLES.insufficient_data;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <span>🔬</span> Patient Specific Phenotype Correlation
        </h3>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.border} ${style.text} border`}>
          {style.label}
        </span>
      </div>

      {/* Summary */}
      <p className="text-slate-600 leading-relaxed text-[15px] mb-4">
        {fit.summary}
      </p>

      {/* Key Factors */}
      {fit.keyFactors && fit.keyFactors.length > 0 && (
        <div className="mb-3">
          <button
            onClick={() => setShowFactors(!showFactors)}
            className="text-sm text-slate-500 hover:text-slate-700 transition-colors flex items-center gap-1"
          >
            <span className="text-xs">{showFactors ? '▼' : '▶'}</span>
            Key Factors ({fit.keyFactors.length})
          </button>
          {showFactors && (
            <ul className="mt-2 space-y-1.5 pl-4">
              {fit.keyFactors.map((factor, idx) => (
                <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                  <span className="text-slate-300 mt-0.5">•</span>
                  <span>{factor}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Penetrance Note */}
      {fit.penetranceNote && (
        <div className="mt-3 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl">
          <p className="text-sm text-blue-700">
            <span className="font-medium">Penetrance: </span>
            {fit.penetranceNote}
          </p>
        </div>
      )}
    </div>
  );
};

export default ClinicalFitCard;
